import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { buildSearchQuery, parseClientSearchParams } from './parsers';
import { useSearchTracker } from '../SearchTrackerProvider';
import { useSearchRequestTimeMetric } from 'clientState/hooks/metrics';
import { SearchRequest } from 'generated/graphql';
import { usePersistedSessionState } from '@edreamsodigeo/frontend-core';

// This attribute is to avoid Ryanair flights to be fetched from
// the back-end again after the user has seen an error in booking
// already, parto of the alternative flight initiative (FC-5692)
export const BOOKING_ERROR_SESSION_KEY = 'bookingError';

export type OF1SearchRequestLocation = {
  numRelatedLocations?: number;
  geoNodeType?: string;
  matchType?: string;
  region?: string;
  type: string;
  text?: string;
  name: string;
  countryCode: string;
  country?: string;
  city?: string;
  iata?: string;
  geoNodeId?: number;
  itemLevel?: string;
  continent: { id: number; name: string };
  coordinates: { latitude?: number | null; longitude?: number | null };
};

export type OF1SearchRequestSegment = {
  dateStr: string;
  departure: OF1SearchRequestLocation;
  destination: OF1SearchRequestLocation;
};

export type CabinClass = 'TOURIST' | 'PREMIUM_ECONOMY' | 'BUSINESS' | 'FIRST';

// Shape of the object received from OF1
export type OF1SearchRequest = {
  buyPath: string;
  cabinClass: CabinClass;
  resident?: boolean;
  mainAirportsOnly: boolean;
  trainSearch: boolean;
  numAdults: number;
  numChildren: number;
  numInfants: number;
  type: 'oneWayTrip' | 'roundTrip' | 'multipleTrip';
  externalSelectionRequest: { searchId?: number };
  directFlightsOnly: boolean;
  airlinesCodes?: string[];
  searchSegments: Array<OF1SearchRequestSegment>;
  internalSearch: boolean;
  creationDate: number;
};

type SearchRequestProviderProps = {
  listenSearchRequestChanges: (
    callback: (searchParams: OF1SearchRequest) => void
  ) => void;
  stopListenSearchRequestChanges?: () => void;
};
export type PerformedSearchRequestContextLocation =
  Partial<OF1SearchRequestLocation> & {
    type: string;
    cityName?: string;
    cityIata?: string;
  };
export type PerformedSearchRequestContextSegment = {
  date: string;
  departure: PerformedSearchRequestContextLocation;
  destination: PerformedSearchRequestContextLocation;
};
export type PerformedSearchRequestContext = {
  buyPath: number;
  tripType: 'ONE_WAY' | 'ROUND_TRIP' | 'MULTIPLE_DESTINATIONS';
  withoutConnections?: boolean;
  trainSearch?: boolean;
  airlineCodes?: string[];
  itinerary: {
    numAdults: number;
    numChildren: number;
    numInfants: number;
    cabinClass: CabinClass;
    resident?: boolean;
    mainAirportsOnly: boolean;
    externalSelection?: { searchId?: number };
    segments: PerformedSearchRequestContextSegment[];
  };
};

export const PerformedSearchRequest =
  createContext<PerformedSearchRequestContext | null>(null);
const SearchRequestProvider: React.FC<SearchRequestProviderProps> = ({
  listenSearchRequestChanges,
  stopListenSearchRequestChanges,
  children,
}) => {
  const { startSearch, endSearch } = useSearchTracker();
  const [searchParams, setSearchParams] = useState<OF1SearchRequest | null>(
    null
  );
  const [bookingError] = usePersistedSessionState(BOOKING_ERROR_SESSION_KEY);
  const { start } = useSearchRequestTimeMetric();
  useEffect(() => {
    listenSearchRequestChanges((receivedSearchParams) => {
      setSearchParams(receivedSearchParams);
      start();
      startSearch();
    });
    return () => {
      stopListenSearchRequestChanges?.();
      endSearch();
    };
  }, [
    listenSearchRequestChanges,
    stopListenSearchRequestChanges,
    startSearch,
    endSearch,
    start,
  ]);

  const performedSearchRequest = useMemo(() => {
    return searchParams
      ? parseClientSearchParams(searchParams, bookingError)
      : null;
  }, [searchParams, bookingError]);

  return (
    <PerformedSearchRequest.Provider value={performedSearchRequest}>
      {children}
    </PerformedSearchRequest.Provider>
  );
};

export default SearchRequestProvider;

export const usePerformedSearchRequest = () =>
  useContext(PerformedSearchRequest);

export const useSearchQuery = (): SearchRequest | null => {
  const searchParams = usePerformedSearchRequest();

  return !searchParams ? null : buildSearchQuery(searchParams);
};

export const useLegByPerformedSearch = (legId) => {
  const { itinerary } = usePerformedSearchRequest() || {};
  const { segments = [] } = itinerary || {};

  return segments[legId] || null;
};

export const useNumberOfPassengers = () => {
  const { itinerary } = usePerformedSearchRequest() || {};
  const { numAdults = 0, numChildren = 0, numInfants = 0 } = itinerary || {};
  return numAdults + numChildren + numInfants;
};

export const useIsResident = () => {
  const {
    itinerary: { resident: isResident },
  } = usePerformedSearchRequest() || { itinerary: { resident: false } };
  return isResident;
};

export const useChildrenInfants = () => {
  const { itinerary } = usePerformedSearchRequest() || {};
  const { numChildren = 0, numInfants = 0 } = itinerary || {};
  const totalChildrenInfant = numChildren + numInfants;
  return totalChildrenInfant;
};

export const useTrainSearch = () => {
  const { trainSearch } = usePerformedSearchRequest() || { trainSearch: false };
  return trainSearch;
};
